<template>
  <div v-if="event">
    <div class="container">
      <div v-if="event.pendingChangeRequest" class="">
        <b-alert show :variant="event.changeRequestType === 'EDIT' ? 'primary' : 'success'" class="mt-3 text-left">
          <h3 class="title">
            A request was made to {{ event.changeRequestType }} this listing on {{ event.changeRequest.updated_at |
              dateAtTime }} by:
          </h3>
          <b v-if="event.changeRequest.updated_by">{{ event.changeRequest.updated_by.first_name }} {{
            event.changeRequest.updated_by.last_name }}</b>
          <b v-else>Deleted User</b>
          <template v-if="event.changeRequest.organization"> | {{ event.changeRequest.organization.name }} </template>
          <br />
          <b v-if="event.changeRequest.updated_by">{{ event.changeRequest.updated_by.telephone | telephoneNumber }} |
            <a :href="'mailto:{{event.changeRequest.updated_by.email}}'">{{ event.changeRequest.updated_by.email
            }}</a></b>
          <div class="mt-3" v-if="$store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'">
            Please review the information in the <strong>PENDING</strong> tab to publish or reject the request. A
            notification will be sent to this member's
            organization upon completion.
          </div>
          <div>
            <b-button size="sm" class="mt-3 btn-white" @click="cancelChangeRequest(event.changeRequestId)">Cancel Change
              Request</b-button>
          </div>
        </b-alert>
      </div>
      <b-card class="mt-5">
        <h1 class="text-center">
          {{ event.name }}
          <router-link
            v-if="(event.changeRequestType != 'ADD' && userIsInOrg()) || $store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'"
            :to="{ name: 'event edit', params: { eventId: event._id } }"
            class="btn btn-secondary event-link-btn"><font-awesome-icon icon="edit" /> Edit</router-link>
        </h1>
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center">
              <span v-if="event.created_at">
                Created: {{ event.created_at | dateOnlyLong }} | Last Updated: {{ event.updated_at | dateOnlyLong }} |
                Version: {{ event.version }}
              </span>
              <span v-else-if="!event.created_at && event.versions && event.versions[0] && event.versions[0].updated_at">
                Created: {{ event.versions[0].updated_at | dateOnlyLong }} | Last Updated:
                {{ event.updated_at | dateOnlyLong }} | Version: {{ event.version }}
              </span>
              <span v-else>
                Created: {{ event.updated_at | dateOnlyLong }} | Last Updated: {{ event.updated_at | dateOnlyLong }} |
                Version: {{ event.version }}
              </span>
              <br />
              <br />
              <template v-if="event.organization">
                <span>Claimed by:
                  <router-link :to="{ name: 'org detail', params: { orgId: event.organization._id } }">{{
                    event.organization.name }} </router-link>
                  <b-button class="btn" @click="claimEvent()">Dispute Claim</b-button></span>
                <br />
              </template>
              <template v-else-if="event.changeRequest && event.changeRequest.organization">
                <span>Claimed by:
                  <router-link :to="{ name: 'org detail', params: { orgId: event.changeRequest.organization._id } }">{{
                    event.changeRequest.organization.name
                  }}</router-link></span>
              </template>
              <template v-else>
                <span class="small"><b>This event has not been claimed.</b></span> <br />
                <span class="small">You may claim this event so your organization can manage it.</span> <br />
                <b-button class="btn" @click="claimEvent()">Claim</b-button>
              </template>
              <span class="small" v-if="event.organization">If you feel this claim is in error, you may dispute the
                ownership of this event by clicking "Dispute Claim" above.</span>
            </div>
          </div>
        </div>
      </b-card>
    </div>
    <div class="container text-left mt-5">
      <div class="row">
        <div class="col-lg-12">
          <b-tabs>
            <b-tab title="Details" active ref="detailsTab">
              <div class="row">
                <div class="col-lg-9">
                  <h2 class="mt-4">Event Detail</h2>
                  <div><b>Publish:</b> {{ event.publish }}</div>
                  <div>
                    <b>Path:</b> <a :href="previewUrl" target="_blank" rel="noopener noreferrer"> {{ event.slug }}</a>
                  </div>
                  <div><b>Counties: </b> {{ event.counties ? event.counties.join(', ') : null }}</div>
                  <div><b>Categories:</b> {{ event.categories ? event.categories.join(', ') : null }}</div>
                  <div>
                    <b>Experience Types:</b>
                    <li v-for="(type, index) in event.experiences" :key="index">
                      {{ type.name }}
                    </li>
                  </div>
                  <div>
                    <b>Badges:</b>
                    <li v-for="(badge, index) in event.badge_info" :key="index">
                      {{ badge.name }}
                    </li>
                  </div>
                  <div v-if="event.website">
                    <div v-if="event.website.includes('http')">
                      <b>Website: </b> <a :href="event.website" alt="Opens in a new window" rel="external noopener noreferrer"
                        target="_blank">{{ event.website }}</a>
                    </div>
                    <div v-else><b>Website: </b>{{ event.website }}</div>
                  </div>
                  <div v-if="event.telephone">
                    <b>Telephone:</b>
                    {{ event.telephone | telephoneNumber }}
                  </div>
                  <h2 class="mt-4">Description</h2>
                  <p><b>Summary</b> {{ event.summary }}</p>
                  <p><b>Full Description</b><span v-html="event.description"></span></p>
                </div>
                <div class="col-lg-3">
                  <h2 class="mt-4">Date</h2>
                  <div><b>Start Date:</b> {{ event.start_on | dateOnlyLong }}</div>
                  <div><b>End Date:</b> {{ event.end_on | dateOnlyLong }}</div>
                  <div><b>Time(s):</b> {{ event.time }}</div>
                  <div><b>Repeats:</b> {{ event.repeats }}</div>

                  <div v-if="event.venue">
                    <h2 class="mt-4">Venue</h2>
                    <div><b>Name: </b> {{ event.venue.name }}</div>
                    <div><b>Address:</b> {{ event.venue.address }}</div>
                    <div><b>City:</b> {{ event.venue.city }}</div>
                    <div>
                      <b>Telephone:</b>
                      {{ event.venue.telephone ? event.venue.telephone : null | telephoneNumber }}
                    </div>
                    <div v-if="event.venue.website">
                      <div v-if="event.venue.website.includes('http')">
                        <b>Website: </b><a :href="event.venue.website" alt="Opens in a new window" rel="external noopener noreferrer"
                          target="_blank">{{ event.venue.website }}</a>
                      </div>
                      <div v-else><b>Website: </b>{{ event.venue.website }}</div>
                    </div>
                  </div>
                  <div v-else>
                    <h2 class="mt-4">Location</h2>
                    <div><b>Address:</b> {{ event.address }}</div>
                    <div><b>City:</b> {{ event.city }}</div>
                    <div>
                      <b>Telephone:</b> {{ event.telephone | telephoneNumber }}
                    </div>
                    <div v-if="event.website">
                      <div v-if="event.website.includes('http')">
                        <b>Website: </b><a :href="event.website" alt="Opens in a new window" rel="external noopener noreferrer"
                          target="_blank">{{ event.website }}</a>
                      </div>
                      <div v-else><b>Website: </b>{{ event.website }}</div>
                    </div>
                  </div>
                  <h2 class="mt-4">Fees</h2>
                  <div><b>Admission Charged:</b> {{ event.cost }}</div>
                  <div><b>Fees:</b> {{ event.fees }}</div>
                  <div v-if="event.tickets_url">
                    <div v-if="event.tickets_url.includes('http')">
                      <b>Ticket Url: </b><a :href="event.tickets_url" alt="Opens in a new window" rel="external noopener noreferrer"
                        target="_blank">{{ event.tickets_url }}</a>
                    </div>
                    <div v-else><b>Ticket Url: </b>{{ event.tickets_url }}</div>
                  </div>
                  <div><b>Cards Accepted: </b> {{ event.credit_cards ? event.credit_cards.join(', ') : null }}</div>

                  <h2 class="mt-4">Social Networks</h2>
                  <div v-for="(network, index) in event.social_networks" :key="index">
                    <b>{{ network.network_name }}:</b> {{ network.account_name }}
                  </div>
                </div>
              </div>
              <div v-if="event.photos || event.photos_for_CR">
                <h2 class="mt-4">Images</h2>
                <div class="row">
                  <div class="col-lg-4" v-for="(photo, index) in event.photos || event.photos_for_CR" :key="index">
                    <b-card :key="photo._id" :title="photo.image_name"
                      :img-src="`https://alabama-travel.s3.amazonaws.com/partners-uploads/photo/image/${photo._id}/${photo.image}`"
                      img-alt="" img-top tag="article" class="mb-4">
                      <b-card-text>
                        <b>Featured:</b> {{ photo.featured }} <br />
                        <b>Description:</b> {{ photo.description }}
                      </b-card-text>
                    </b-card>
                  </div>
                </div>
              </div>
            </b-tab>
            <b-tab title="Preview" v-if="event.changeRequestType === 'ADD'"> <iframe :src="pendingApprovalPreviewUrl"
                class="preview-frame" /></b-tab>
            <b-tab title="Preview" v-else> <iframe :src="previewUrl" class="preview-frame" /></b-tab>
            <b-tab title="History">
              <div class="container">
                <div class="row">
                  <div class="col-12 text-left mt-4" v-if="event.updated_by && event.version">
                    <span class="">Current Version is {{ event.version }} by {{ event.updated_by.first_name }} {{
                      event.updated_by.last_name }}</span>
                  </div>
                  <div class="col-12 text-left" v-if="event.versions">
                    <table class="table table-bordered" v-if="event.reversedVersions">
                      <template v-for="(version, versionKey) in event.reversedVersions">
                        <thead :key="versionKey">
                          <tr>
                            <th colspan="3" class="text-center" v-if="version.updated_by">
                              Version: {{ version.version }} changed on {{ version.updated_at | dateAtTime }}
                              by {{ version.updated_by.first_name }} {{ version.updated_by.last_name }}
                            </th>
                          </tr>
                        </thead>
                        <tbody :key="versionKey">
                          <tr>
                            <td class="td-parent" colspan="1"><b>Field</b></td>
                            <td class="td-parent" colspan="1">
                              <b>Old</b>
                              <span v-if="versionKey < event.reversedVersions.length - 1"> ({{
                                event.reversedVersions[versionKey + 1]['updated_by'] }})</span>
                            </td>
                            <td class="td-parent" colspan="1"><b>New</b> ({{ version.updated_by }})</td>
                          </tr>
                          <tr class="mb-1" v-for="(key, index) in Object.keys(version)" :key="index">
                            <template v-if="versionKey < event.reversedVersions.length - 1 &&
                              hasValueChanged(key, version[key], event.reversedVersions[versionKey + 1][key])
                              ">
                              <td class="td-child" colspan="1">{{ key }}</td>
                              <td v-if="typeof event.reversedVersions[versionKey + 1][key] !== 'object'" class="td-child"
                                colspan="1">
                                {{ event.reversedVersions[versionKey + 1][key] }}
                              </td>
                              <td v-else class="td-child" colspan="1">
                                <!--old value-->
                                <ul>
                                  <li v-for="(val, index) in event.reversedVersions[versionKey + 1][key]" :key="index">
                                    <span v-if="key === 'experience_types'">{{ val.name.toString() }}</span>
                                    <span v-else>{{ val }}</span>
                                  </li>
                                </ul>
                              </td>
                              <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{ version[key] }}
                              </td>
                              <td v-else class="td-child" colspan="1">
                                <!--new value-->
                                <ul>
                                  <li v-for="(val, index) in version[key]" :key="index">
                                    <span v-if="key === 'experience_types'">{{ val.name.toString() }}</span>
                                    <span v-else>{{ val }}</span>
                                  </li>
                                </ul>
                              </td>
                            </template>
                            <template v-if="versionKey && versionKey == event.reversedVersions.length - 1">
                              <td class="td-child" colspan="1">{{ key }}</td>
                              <td></td>
                              <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{ version[key] }}
                              </td>
                              <td v-else class="td-child" colspan="1">
                                <ul>
                                  <li v-for="(val, index) in version[key]" :key="index">
                                    {{ val }}
                                  </li>
                                </ul>
                              </td>
                            </template>
                          </tr>
                        </tbody>
                      </template>
                    </table>
                  </div>
                </div>
              </div>
            </b-tab>
            <b-tab title="Pending" v-if="event.pendingChangeRequest">
              <div class="actions text-right mt-2 mb-2"
                v-if="$store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'">
                <b-button class="mr-1"
                  v-on:click="approveChangeRequest(event.changeRequest._id, event._id)">Approve</b-button>
                <b-button class="mr-3" v-on:click="rejectChangeRequest(event.changeRequest._id)">Reject</b-button>
              </div>
              <div class="container">
                <div class="row">
                  <div class="col-12 text-left">
                    <table class="table table-bordered">
                      <template>
                        <thead>
                          <tr v-if="event.reversedVersions">
                            <th colspan="3" class="text-center">
                              Version: {{ event.changeRequest.version }} changed on {{
                                event.changeRequest.updated_at | dateAtTime }}
                            </th>
                          </tr>
                        </thead>
                        <tbody v-if="event.reversedVersions">
                          <tr>
                            <td class="td-parent" colspan="1"><b>Field</b></td>
                            <td class="td-parent" colspan="1">
                              <b>Current</b>
                              <span v-if="versionKey < event.reversedVersions.length - 1"> ({{
                                event.reversedVersions[versionKey + 1]['updated_by'] }})</span>
                            </td>
                            <td class="td-parent" colspan="1"><b>New</b></td>
                          </tr>

                          <tr class="mb-1" v-for="(key, index) in Object.keys(event)" :key="index">
                            <template v-if="hasValueChanged(key, event.changeRequest.listing[key], event[key])">
                              <td class="td-child" colspan="1">{{ key }}</td>
                              <!-- CURRENT or oldValues -->
                              <td v-if="key === 'experience_types'" class="td-child" colspan="1">{{ event[key].map((x) =>
                                x.name).toString() }}</td>
                              <td v-else-if="key === 'photos'" class="td-child" colspan="1">
                                <b>Number of Photos : {{ event[key].length }}</b><br />{{ event[key] }}
                              </td>
                              <td v-else class="td-child" colspan="1">{{ event[key] }}</td>
                              <!-- NEW or newValues -->
                              <td v-if="key === 'start_on'" class="td-child" colspan="1">
                                {{ event.changeRequest.listing[key] | dateOnly }}
                              </td>
                              <td v-else-if="key === 'end_on'" class="td-child" colspan="1">
                                {{ event.changeRequest.listing[key] | dateOnly }}
                              </td>
                              <td v-else-if="key === 'experience_types'" class="td-child" colspan="1">
                                {{ event.changeRequest.listing[key].map((x) => x.name).toString() }}
                              </td>
                              <td v-else-if="key === 'photos'" class="td-child" colspan="1">
                                <b>Number of Photos : {{ event.changeRequest.listing[key].length }}</b><br />{{
                                  event.changeRequest.listing[key] }}
                              </td>
                              <td v-else class="td-child" colspan="1">{{ event.changeRequest.listing[key] }}</td>
                              <!-- <td v-else class="td-child" colspan="1">
                                <ul>
                                  <li v-for="(val, index) in version[key]" :key="index">
                                   {{val}}
                                  </li>
                                </ul>
                              </td> -->
                            </template>
                            <!-- <template v-if="versionKey == event.reversedVersions.length -1">
                            <td class="td-child" colspan="1">{{key}}</td>
                            <td></td>
                             <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{version[key]}}</td>
                              <td v-else class="td-child" colspan="1">
                                <ul>
                                  <li v-for="(val, index) in version[key]" :key="index">
                                   {{val}}
                                  </li>
                                </ul>
                              </td>
                          </template> -->
                          </tr>
                        </tbody>
                        <!-- This is a brand new event -->
                        <tbody v-else>
                          <tr>
                            <td class="td-parent" colspan="1"><b>Field</b></td>
                            <td class="td-parent" colspan="1"><b>Value</b></td>
                          </tr>
                          <tr class="mb-1" v-for="(key, index) in Object.keys(event)" :key="index">
                            <td class="td-child" colspan="1">{{ key }}</td>
                            <td class="td-child" colspan="1">{{ event[key] }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </table>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
    <b-modal id="org-modal" title="What Organization Are You Claiming This For?">
      <b-form-select id="org" name="org" v-model="chooserOrg">
        <option :value="null">Select Organization</option>
        <option v-for="(org, index) in userOrgs" :value="org.value" :key="index">{{ org.text }}</option>
      </b-form-select>

      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel()"> Cancel </b-button>
        <b-button size="sm" variant="success" @click="claimEventForOrg()"> Claim </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import deepEqual from 'deep-equal';

export default {
  data() {
    return {
      event: {},
      userOrgs: [],
      chooserOrg: null,
    };
  },
  props: {
    eventId: String,
  },
  async created() {
    await this.loadEventDetails();
  },
  computed: {
    previewUrl() {
      return `${process.env.VUE_APP_WEB_ROOT}upcoming-events/${this.event.slug}`;
    },
    pendingApprovalPreviewUrl() {
      return `${process.env.VUE_APP_WEB_ROOT}upcoming-events/${this.event.slug}/preview`;
    },
  },
  methods: {
    userIsInOrg() {
      if (this.event.organization) {
        return this.event.organization.user_ids.find((u) => u === this.$store.state.auth.mUser._id);
      }
      return false;
    },
    async loadEventDetails() {
      if (!this.eventId) {
        return;
      }
      const eventResponse = await axios.get(`${process.env.VUE_APP_API_EVENTS_ROOT}/events/${this.eventId}`);
      this.event = eventResponse.data;

      if (this.event && this.event.versions) {
        this.event.reversedVersions = this.event.versions.slice().reverse();
      }
      // If this is a changeRequest and a telephone number update is  pending, and the event object does not have a telephone key, we add one. -CSD
      if (
        this.event.pendingChangeRequest === true
        && Object.prototype.hasOwnProperty.call(this.event, 'telephone') === false
        && Object.prototype.hasOwnProperty.call(this.event.changeRequest.listing, 'telephone') === true
      ) {
        this.event.telephone = '';
      }

      // process current Event's experience_types if unformatted.
      if (!Object.prototype.hasOwnProperty.call(this.event.experience_types[0], 'value')) {
        this.event.experience_types_unformatted = this.event.experience_types;
        this.event.experience_types_formatted = [];
        this.event.experience_types = this.getExperienceTypes(this.event.experience_types_formatted, this.event.experience_types_unformatted);
      }

      // if exp types non-existent or [] set an ad hoc experience type of 'Not Set' so that
      // downstream elements hasValueChanged elements won't error when trying to call toString() on undefined.
      if (!this.event.experience_types || this.event.experience_types.length === 0) {
        this.event.experience_types = [];
        const subValues = { value: 11, name: 'Not Set' };
        this.event.experience_types.push(subValues);
      }

      // process Event's change request experience_types if unformatted.
      if (this.event.changeRequest && this.event.changeRequest.listing !== null) {
        // if exp types null or [] -- set exp types in CR listing object to a default value to prevent hasValueChanged comparison errors.
        if (!this.event.changeRequest.listing.experience_types || this.event.changeRequest.listing.experience_types.length === 0) {
          this.event.changeRequest.listing.experience_types = [];
          const subValues = { value: 11, name: 'Not Set' };
          this.event.changeRequest.listing.experience_types.push(subValues);
        }
        if (!Object.prototype.hasOwnProperty.call(this.event.changeRequest.listing.experience_types[0], 'value')) {
          this.event.changeRequest.listing.experience_types_unformatted = this.event.changeRequest.listing.experience_types;
          this.event.changeRequest.listing.experience_types_formatted = [];
          this.event.changeRequest.listing.experience_types = this.getExperienceTypes(
            this.event.changeRequest.listing.experience_types_formatted,
            this.event.changeRequest.listing.experience_types_unformatted
          );
        }
        // Create an ad hoc sibling property named 'photos' and assign the value of photos_for_CR, so that hasValueChanged function can properly evaluate.
        this.event.changeRequest.listing.photos = this.event.changeRequest.listing.photos_for_CR;
      }

      // Mutate each of the reversedVersion's experience_typs array, removing 0, null, blank, false, undefined and NaN values - CSD
      if (this.event.reversedVersions) {
        this.event.reversedVersions.forEach((element) => {
          let index = -1;
          const arrLength = element.experience_types ? element.experience_types.length : 0;
          let resIndex = -1;
          const result = [];
          while (++index < arrLength) {
            const value = element.experience_types[index];
            if (value) {
              result[++resIndex] = value;
            }
          }
          element.experience_types = result;
        });
      }

      /* reversedVersions is a large object with multiple Event versions. Map each of the version's experience types, if it doesnt have property name 'value', then
         call function to modify the version's experience_types to name/value format. --CSD
      */
      if (this.event.reversedVersions) {
        this.event.reversedVersions.map((x) => (!Object.prototype.hasOwnProperty.call(x.experience_types, 'value') ? this.formatExperienceTypes(x.experience_types) : x.experience_types));
      }
    },
    async rejectChangeRequest(requestId) {
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}/admin/rejectChangeRequest/${requestId}`);
      if (response.data.success) {
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Success',
          autoHideDelay: 5000,
          appendToast: true,
        });

        await this.$store.dispatch('changeRequests/getPendingEvents');
        await this.loadEventDetails();
        this.$refs.detailsTab.activate();
      } else {
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Error',
          autoHideDelay: 5000,
          appendToast: true,
        });
      }
    },
    async approveChangeRequest(requestId, eventId) {
      const requestInfo = {
        change_request_id: requestId,
        event_id: eventId,
      };
      await axios.post(`${process.env.VUE_APP_API_EVENTS_ROOT}/events/approveEventChangeRequest`, JSON.stringify(requestInfo)).then(
        async (response) => {
          const changeResponse = await axios.get(`${process.env.VUE_APP_API_ROOT}/admin/approveChangeRequest/${requestId}`);
          if (changeResponse.data.success) {
            const self = this;
            if (!requestInfo.event_id) {
              // Event ADD type
              this.$router.push({ name: 'event detail', params: { eventId: response.data.eventId } }, () => {
                setTimeout(async () => {
                  self.$bvToast.toast(`${JSON.stringify(response.data.message)}`, { title: 'Success', autoHideDelay: 5000, appendToast: true });
                  await this.$store.dispatch('changeRequests/getPendingEvents');
                  await this.loadEventDetails();
                  this.$refs.detailsTab.activate();
                }, 500);
              });
            } else {
              // Event EDIT type
              setTimeout(async () => {
                self.$bvToast.toast(`${response.data.message}`, { title: 'Success', autoHideDelay: 5000, appendToast: true });
                await this.$store.dispatch('changeRequests/getPendingEvents');
                await this.loadEventDetails();
                this.$refs.detailsTab.activate();
              }, 500);
            }
          } else {
            this.$bvToast.toast(`${changeResponse.data.message}`, {
              title: 'Error',
              autoHideDelay: 5000,
              appendToast: true,
            });
          }
        },
        (error) => {
          this.$bvToast.toast(`${error.response.data}`, {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      );
    },
    async cancelChangeRequest(changeRequestId) {
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}/admin/cancelChangeRequest/${changeRequestId}`);
      if (response.data.success) {
        this.event.pendingChangeRequest = false;
        this.event.changeRequestId = null;
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Success',
          autoHideDelay: 5000,
          appendToast: true,
        });
      } else {
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Error',
          autoHideDelay: 5000,
          appendToast: true,
        });
      }
    },
    hasValueChanged(key, newVal, oldVal) {
      if (
        key === 'version'
        || key === 'updated_at'
        || key === 'updated_by'
        || key === 'reversedVersions'
        || key === 'changeRequest'
        || key === 'changeRequestId'
        || key === 'pendingChangeRequest'
        || key === 'versions'
        || key === 'organization'
        || key === 'changeRequestType'
        || key === 'experiences'
        || key === 'experiences_ids'
        || key === '__v'
      ) {
        return false;
      }
      if ((typeof oldVal !== 'object' || typeof newVal !== 'object') && newVal !== oldVal && newVal !== undefined) {
        if (key === 'start_on' || key === 'end_on') {
          // normalize all incoming datetimes to mm/dd/yyyy
          const newValDate = new Date(newVal).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
          const oldValDate = new Date(oldVal).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

          // if newValdate and oldValdate are the same, return false, "no change"
          if (newValDate === oldValDate) {
            return false;
          }
        }
        return true;
      }
      if (typeof oldVal === 'object' || typeof newVal === 'object') {
        const isEqual = deepEqual(newVal, oldVal);
        // if  obj equality is false, oldVal and newVal are different and should be displayed
        if (oldVal && newVal && isEqual === false) {
          return !oldVal.every((e) => newVal.includes(e));
        }
      }
      return false;
    },
    async claimEvent() {
      const response = await axios.get(`${process.env.VUE_APP_API_ORGANIZATIONS_ROOT}/getOrgsForUser/${this.$store.state.auth.mUser._id}`);
      const orgs = response.data.userOrganizations;
      this.userOrgs = orgs.map((o) => ({ text: o.name, value: o._id }));

      if (this.userOrgs.length === 0) {
        // eslint-disable-next-line no-alert
        alert('You must be a member of an organization to submit a claim request.');
      } else if (this.userOrgs.length > 1) {
        this.$bvModal.show('org-modal');
      } else {
        const claimResponse = await axios.post(`${process.env.VUE_APP_API_EVENTS_ROOT}/claimEvent`, {
          organizationId: this.userOrgs[0].value,
          eventId: this.eventId,
        });
        if (claimResponse.status === 200) {
          this.$bvToast.toast('Claim successfully submitted.');
        } else {
          this.$bvToast.toast('Something went wrong in submitting the claim.  Please try again later.');
        }
      }
    },
    async claimEventForOrg() {
      this.$bvModal.hide('org-modal');
      const claimResponse = await axios.post(`${process.env.VUE_APP_API_EVENTS_ROOT}/claimEvent`, {
        organizationId: this.chooserOrg,
        eventId: this.eventId,
      });
      if (claimResponse.status === 200) {
        this.$bvToast.toast('Claim successfully submitted.');
      } else {
        this.$bvToast.toast('Something went wrong in submitting the claim.  Please try again later.');
      }
    },
    getExperienceTypes(obj, experienceTypes) {
      if (experienceTypes) {
        experienceTypes.forEach((type) => {
          switch (type) {
            case 1:
              obj.push({ value: 1, name: 'History' });
              break;
            case 2:
              obj.push({ value: 2, name: 'Beaches' });
              break;
            case 3:
              obj.push({ value: 3, name: 'Food' });
              break;
            case 4:
              obj.push({ value: 4, name: 'Arts & Culture' });
              break;
            case 5:
              obj.push({ value: 5, name: 'Civil Rights' });
              break;
            case 6:
              obj.push({ value: 6, name: 'Music' });
              break;
            case 7:
              obj.push({ value: 7, name: 'Golf' });
              break;
            case 8:
              obj.push({ value: 8, name: 'Outdoors' });
              break;
            case 9:
              obj.push({ value: 9, name: 'Science & Discovery' });
              break;
            case 10:
              obj.push({ value: 10, name: 'Sports' });
              break;
            case 0:
              obj.push({ value: 11, name: 'Not Set' });
              break;
            default:
              break;
          }
        });
      }
      return obj;
    },
    formatExperienceTypes(experienceTypes) {
      if (experienceTypes) {
        experienceTypes.forEach((type, index) => {
          switch (type) {
            case 1:
              experienceTypes[index] = { value: 1, name: 'History' };
              break;
            case 2:
              experienceTypes[index] = { value: 2, name: 'Beaches' };
              break;
            case 3:
              experienceTypes[index] = { value: 3, name: 'Food' };
              break;
            case 4:
              experienceTypes[index] = { value: 4, name: 'Arts & Culture' };
              break;
            case 5:
              experienceTypes[index] = { value: 5, name: 'Civil Rights' };
              break;
            case 6:
              experienceTypes[index] = { value: 6, name: 'Music' };
              break;
            case 7:
              experienceTypes[index] = { value: 7, name: 'Golf' };
              break;
            case 8:
              experienceTypes[index] = { value: 8, name: 'Outdoors' };
              break;
            case 9:
              experienceTypes[index] = { value: 9, name: 'Science & Discovery' };
              break;
            case 10:
              experienceTypes[index] = { value: 10, name: 'Sports' };
              break;
            case 0:
              experienceTypes[index] = { value: 11, name: 'Not Set' };
              break;
            default:
              break;
          }
        });
      }
      return experienceTypes;
    },
  },
};
</script>

<style scoped>
.preview-frame {
  width: 100%;
  height: 100vh;
}

.event-link-btn {
  margin: 0 5px;
}

table th {
  background-color: aliceblue;
  margin-top: 20px;
}

.alert-success .btn-secondary {
  background-color: #155724;
}

.card-body h1 {
  margin: 0px 0 10px 0;
}
</style>
